import apiClient from "./apiClient";

/**
 * Fetch all employees for a tenant
 */
export const getEmployees = async (companyCode) => {
  try {
    const res = await apiClient.get("/api/employees", {
      params: companyCode ? { companyCode } : {},
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (error) {
    console.error("getEmployees error:", error);
    return { success: false, message: error.message, employees: [] };
  }
};

/**
 * Create a new employee
 */
export const createEmployee = async (employeeData, companyCode) => {
  try {
    const res = await apiClient.post(
      "/api/employees",
      { ...employeeData, companyCode },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (error) {
    console.error("createEmployee error:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Update an existing employee
 */
export const updateEmployee = async (employeeId, employeeData, companyCode) => {
  try {
    const res = await apiClient.put(
      `/api/employees/${employeeId}`,
      employeeData,
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (error) {
    console.error("updateEmployee error:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Delete an employee
 */
export const deleteEmployee = async (employeeId, companyCode) => {
  try {
    const res = await apiClient.delete(`/api/employees/${employeeId}`, {
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (error) {
    console.error("deleteEmployee error:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Update employee tags
 */
export const updateEmployeeTags = async (employeeId, tags, companyCode) => {
  try {
    const res = await apiClient.patch(
      `/api/employees/${employeeId}/tags`,
      { tags, companyCode },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (error) {
    console.error("updateEmployeeTags error:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Trigger sync for a single employee
 */
export const triggerEmployeeSync = async (employeeId, companyCode) => {
  try {
    const res = await apiClient.post(
      `/api/employees/${employeeId}/sync-triggers`,
      {},
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (error) {
    console.error("triggerEmployeeSync error:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Trigger company-wide sync for all employees
 */
export const triggerCompanySync = async (companyCode) => {
  try {
    const res = await apiClient.post(
      "/api/employees/sync-triggers",
      { companyCode },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (error) {
    console.error("triggerCompanySync error:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Get individual employee call stats for a period
 */
export const getEmployeeCallStats = async (companyCode, employeeId, period, from, to) => {
  try {
    const effectivePeriod = period || "today";
    const params = {};
    if (companyCode) params.companyCode = companyCode;
    if (employeeId) params.employeeId = employeeId;
    if (from) {
      params.from = from;
      if (to) params.to = to;
    } else if (effectivePeriod) {
      params.period = effectivePeriod;
    }

    const res = await apiClient.get("/api/call-logs/employee", {
      params,
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (error) {
    console.error("getEmployeeCallStats error:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Get individual call details / history for an employee
 */
export const getEmployeeCallDetails = async (companyCode, employeeId, period, from, to, search) => {
  try {
    const effectivePeriod = period || "today";
    const params = {};
    if (companyCode) params.companyCode = companyCode;
    if (employeeId) params.employeeId = employeeId;
    if (from) {
      params.from = from;
      if (to) params.to = to;
    } else if (effectivePeriod) {
      params.period = effectivePeriod;
    }
    if (search) params.search = search;

    const res = await apiClient.get("/api/call-logs/details", {
      params,
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (error) {
    console.error("getEmployeeCallDetails error:", error);
    return { success: false, message: error.message, calls: [] };
  }
};

/**
 * Get annual target & achieved revenue breakdown for an employee
 */
export const getEmployeeAnnualTargets = async (companyCode, employeeId, year) => {
  try {
    const res = await apiClient.get(`/api/targets/employee/${employeeId}`, {
      params: { companyCode, year: year || new Date().getFullYear() },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (error) {
    console.error("getEmployeeAnnualTargets error:", error);
    return { success: false, message: error.message, data: [] };
  }
};

/**
 * Get monthly targets and revenues for all employees
 */
export const getMonthlyTargets = async (companyCode, year, month) => {
  try {
    const now = new Date();
    const res = await apiClient.get("/api/targets", {
      params: {
        companyCode,
        year: year || now.getFullYear(),
        month: month || now.getMonth() + 1,
      },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (error) {
    console.error("getMonthlyTargets error:", error);
    return { success: false, message: error.message, data: [] };
  }
};

/**
 * Set target for an employee
 */
export const setEmployeeTarget = async (companyCode, employeeId, year, month, targetAmount) => {
  try {
    const res = await apiClient.post(
      "/api/targets",
      { companyCode, employeeId, year, month, targetAmount },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (error) {
    console.error("setEmployeeTarget error:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Reset 2FA for an employee so they can set up a new QR code on next login
 */
export const resetEmployee2FA = async (employeeId, companyCode) => {
  try {
    const res = await apiClient.post(
      `/api/employees/${employeeId}/two-factor-auth-resets`,
      {},
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (error) {
    console.error("resetEmployee2FA error:", error);
    return { success: false, message: error.message || "Failed to reset 2FA" };
  }
};

/**
 * Update employee profile photo
 */
export const updateEmployeeProfilePhoto = async (employeeId, profilePhoto, companyCode) => {
  try {
    const res = await apiClient.patch(
      `/api/employees/${employeeId}/profile-photo`,
      { profilePhoto, companyCode },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (error) {
    console.error("updateEmployeeProfilePhoto error:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Lightweight fetch of employee profile photos map { [id/name]: profilePhoto }
 */
export const getEmployeeProfilePhotos = async (companyCode) => {
  try {
    const res = await apiClient.get("/api/employees/profile-photos", {
      params: companyCode ? { companyCode } : {},
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (error) {
    console.warn("getEmployeeProfilePhotos error:", error);
    return { success: false, photos: {} };
  }
};
